import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useUserName } from '../../contexts/userName';
import CustomFilterSelect from '../../shared/customFilterSelect';
import {
  createLogisticsBag,
  getLogisticsBagContext,
  getLogisticsRequestHistory,
  getLogisticsRequests,
  reviewLogisticsRequest
} from '../../API/apiService';
import './style.scss';

const FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'returned', label: 'En subsanación' },
  { value: 'accepted', label: 'Aceptadas' },
  { value: 'cancelled', label: 'Canceladas' }
];
const STATUS_LABELS = {
  pending: 'Pendiente', returned: 'En subsanación', accepted: 'Aceptada', cancelled: 'Cancelada'
};
const HISTORY_LABELS = {
  created: 'Creó la solicitud', edited: 'Editó la solicitud',
  returned: 'Solicitó la subsanación', accepted: 'Aceptó la solicitud',
  cancelled: 'Canceló la solicitud',
  checklist_uploaded: 'Cargó o reemplazó la lista de chequeo'
};

function LogisticsDashboard() {
  const { userName } = useUserName();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState('accepted');
  const [code, setCode] = useState('');
  const [observation, setObservation] = useState('');
  const [approvalPdf, setApprovalPdf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRequest, setHistoryRequest] = useState(null);
  const [showBagCreator, setShowBagCreator] = useState(false);
  const [bagContext, setBagContext] = useState({ mediators: [], bags: [] });
  const [bagContextLoading, setBagContextLoading] = useState(false);
  const [bagSaving, setBagSaving] = useState(false);
  const [bagForm, setBagForm] = useState({ mediatorIds: [], code: '', eventName: '' });
  const [cancellingId, setCancellingId] = useState(null);
  const firstName = String(userName || 'Logística').trim().split(/\s+/)[0];

  const visibleRequests = useMemo(
    () => filter === 'all' ? requests : requests.filter(item => item.status === filter),
    [filter, requests]
  );
  const pendingCount = requests.filter(item => item.status === 'pending').length;
  const bagMediatorLabel = useMemo(() => {
    const names = bagContext.mediators
      .filter(item => bagForm.mediatorIds.includes(Number(item.id)))
      .map(item => item.name);
    if (names.length === 0) return 'Selecciona uno o varios mediadores';
    if (names.length <= 2) return names.join(', ');
    return `${names.length} mediadores seleccionados`;
  }, [bagContext.mediators, bagForm.mediatorIds]);

  async function loadRequests() {
    setLoading(true);
    try {
      setRequests(await getLogisticsRequests());
    } catch (error) {
      toast('No fue posible cargar los requerimientos', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRequests(); }, []);

  async function openBagCreator() {
    setShowBagCreator(true);
    setBagForm({ mediatorIds: [], code: '', eventName: '' });
    setBagContextLoading(true);
    try {
      setBagContext(await getLogisticsBagContext());
    } catch (error) {
      toast('No fue posible cargar los mediadores', { type: 'error' });
      setShowBagCreator(false);
    } finally {
      setBagContextLoading(false);
    }
  }

  function closeBagCreator() {
    if (bagSaving) return;
    setShowBagCreator(false);
    setBagForm({ mediatorIds: [], code: '', eventName: '' });
  }

  function selectBagMediators(_key, mediatorIds) {
    setBagForm(current => ({ ...current, mediatorIds: mediatorIds.map(Number) }));
  }

  async function submitBag(event) {
    event.preventDefault();
    if (bagForm.mediatorIds.length === 0) {
      toast('Selecciona al menos un mediador', { type: 'warning' });
      return;
    }
    setBagSaving(true);
    try {
      const created = await createLogisticsBag(bagForm);
      setBagContext(current => ({
        ...current,
        bags: created ? [created, ...current.bags] : current.bags
      }));
      toast('Bolsa creada y asignada a los mediadores', { type: 'success' });
      setShowBagCreator(false);
      setBagForm({ mediatorIds: [], code: '', eventName: '' });
    } catch (error) {
      const message = error?.response?.data?.message;
      toast(
        message === 'LOGISTICS_BAG_CODE_ALREADY_EXISTS'
          ? 'Ya existe una bolsa con ese código de solicitud'
          : 'No fue posible crear la bolsa. Revisa los campos.',
        { type: 'error' }
      );
    } finally {
      setBagSaving(false);
    }
  }

  function openReview(item) {
    setSelected(item);
    setAction(item.status === 'returned' ? 'returned' : 'accepted');
    setCode(item.code || '');
    setObservation(item.reviewObservation || '');
    setApprovalPdf(null);
  }

  async function openHistory(item) {
    setHistoryRequest(item);
    setHistory([]);
    setHistoryLoading(true);
    try {
      setHistory(await getLogisticsRequestHistory(item.id));
    } catch (error) {
      toast('No fue posible cargar el historial de la solicitud', { type: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }

  async function cancelRequest(item) {
    const confirmed = window.confirm(
      `¿Seguro que deseas cancelar la solicitud ${item.code || `#${item.id}`}? Esta acción quedará registrada en el historial.`
    );
    if (!confirmed) return;

    setCancellingId(item.id);
    try {
      const updated = await reviewLogisticsRequest(item.id, {
        action: 'cancelled', code: item.code || '', observation: '', approvalPdf: null
      });
      setRequests(current => current.map(request =>
        request.id === updated.id ? updated : request
      ));
      toast('Solicitud cancelada', { type: 'success' });
    } catch (error) {
      toast('No fue posible cancelar la solicitud', { type: 'error' });
    } finally {
      setCancellingId(null);
    }
  }

  function closeReview() {
    setSelected(null);
    setCode('');
    setObservation('');
    setApprovalPdf(null);
  }

  function closeHistory() {
    setHistoryRequest(null);
    setHistory([]);
  }

  function selectChecklist(event) {
    const file = event.target.files?.[0] || null;
    if (file && file.size > 4 * 1024 * 1024) {
      event.target.value = '';
      setApprovalPdf(null);
      toast('La lista de chequeo debe pesar máximo 4 MB', { type: 'error' });
      return;
    }
    setApprovalPdf(file);
  }

  async function submitReview(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const updated = await reviewLogisticsRequest(selected.id, {
        action, code, observation, approvalPdf
      });
      setRequests(current => current.map(item => item.id === updated.id ? updated : item));
      const checklistUpdated = selected.status === 'accepted' && action === 'accepted' && approvalPdf;
      toast(
        checklistUpdated
          ? 'Lista de chequeo guardada'
          : action === 'accepted' ? 'Solicitud aceptada' : 'Solicitud enviada a subsanación',
        { type: 'success' }
      );
      closeReview();
    } catch (error) {
      const message = error?.response?.data?.message;
      const friendlyMessage = message === 'REQUEST_CODE_ALREADY_EXISTS'
        ? 'El código ingresado ya pertenece a otra solicitud'
        : message === 'REQUEST_CODE_REQUIRED'
          ? 'Debes asignar un código antes de aceptar'
          : message === 'APPROVAL_PDF_INVALID'
              ? 'La lista de chequeo debe ser un PDF de máximo 4 MB'
              : 'No fue posible guardar la revisión';
      toast(friendlyMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className='logistics-dashboard'>
      <div className='logistics-dashboard__content'>
        <header className='logistics-dashboard__welcome'>
          <div>
            <span className='logistics-dashboard__eyebrow'>GESTIÓN LOGÍSTICA</span>
            <h1>Hola, {firstName}</h1>
            <p>Revisa, acepta o solicita la subsanación de los requerimientos enviados por los mediadores.</p>
          </div>
          <div className='logistics-dashboard__welcome-actions'>
            <button type='button' onClick={openBagCreator}>
              <span className='material-symbols-rounded'>add</span>Crear Bolsa
            </button>
            <div className='logistics-dashboard__status'><span></span>{pendingCount} pendientes</div>
          </div>
        </header>

        <section className='logistics-dashboard__workspace'>
          <div className='logistics-dashboard__heading'>
            <div>
              <span className='logistics-dashboard__eyebrow'>BANDEJA DE ENTRADA</span>
              <h2>Solicitudes</h2>
            </div>
            <button type='button' onClick={loadRequests} disabled={loading}>
              <span className='material-symbols-rounded'>refresh</span>Actualizar
            </button>
          </div>

          <div className='logistics-dashboard__filters' role='tablist' aria-label='Filtrar solicitudes'>
            {FILTERS.map(item => (
              <button
                type='button'
                role='tab'
                aria-selected={filter === item.value}
                className={filter === item.value ? 'is-active' : ''}
                onClick={() => setFilter(item.value)}
                key={item.value}>
                {item.label}
                <span>{item.value === 'all' ? requests.length : requests.filter(request => request.status === item.value).length}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className='logistics-dashboard__empty'>Cargando solicitudes...</div>
          ) : visibleRequests.length === 0 ? (
            <div className='logistics-dashboard__empty'>
              <span className='material-symbols-rounded'>inbox</span>No hay solicitudes en este estado.
            </div>
          ) : (
            <div className='logistics-dashboard__requests'>
              {visibleRequests.map(item => (
                <article className='logistics-card' key={item.id}>
                  <div className='logistics-card__top'>
                    <span className='material-symbols-rounded'>{item.requestType === 'bag_request' ? 'package_2' : 'event_note'}</span>
                    <div>
                      <span className='logistics-card__type'>{item.requestType === 'bag_request' ? 'SOLICITUD DE BOLSA' : 'NUEVO REQUERIMIENTO'}</span>
                      <h3>{item.eventName || `Entrega #${item.deliveryNumber}`}</h3>
                    </div>
                    <span className={`logistics-card__status request-status--${item.status}`}>{STATUS_LABELS[item.status]}</span>
                  </div>
                  <dl>
                    <div><dt>Responsable</dt><dd>{item.responsibleName}</dd></div>
                    <div><dt>Lugar</dt><dd>{item.place}</dd></div>
                    <div><dt>Fecha / hora</dt><dd>{item.requestDate || 'Sin fecha'} · {item.requestTime}</dd></div>
                    <div><dt>Código</dt><dd>{item.code || 'Por asignar'}</dd></div>
                  </dl>
                  <div className='logistics-card__actions'>
                    <button type='button' onClick={() => openHistory(item)}>
                      <span className='material-symbols-rounded'>history</span>Ver historial
                    </button>
                    {item.status !== 'cancelled' && (
                      <>
                        <button
                          type='button'
                          className='is-cancel'
                          onClick={() => cancelRequest(item)}
                          disabled={cancellingId === item.id}>
                          <span className='material-symbols-rounded'>cancel</span>
                          {cancellingId === item.id ? 'Cancelando...' : 'Cancelar'}
                        </button>
                        <button type='button' onClick={() => openReview(item)}>
                          Ver y revisar <span className='material-symbols-rounded'>arrow_forward</span>
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {showBagCreator && (
        <div className='logistics-review' role='presentation' onMouseDown={event => {
          if (event.target === event.currentTarget) closeBagCreator();
        }}>
          <form className='logistics-review__panel logistics-review__panel--bag' onSubmit={submitBag} role='dialog' aria-modal='true' aria-labelledby='bag-title'>
            <header>
              <div>
                <span className='logistics-dashboard__eyebrow'>ASIGNACIÓN LOGÍSTICA</span>
                <h2 id='bag-title'>Crear Bolsa</h2>
                <p>Asigna la misma bolsa a uno o varios mediadores.</p>
              </div>
              <button type='button' onClick={closeBagCreator} aria-label='Cerrar'>×</button>
            </header>

            {bagContextLoading ? (
              <div className='logistics-review__loading'>Cargando mediadores...</div>
            ) : (
              <div className='logistics-review__bag-fields'>
                <label className='logistics-review__field logistics-review__mediators'>
                  <span>Mediador *</span>
                  <CustomFilterSelect
                    optionsList={bagContext.mediators}
                    placeholder='Selecciona uno o varios mediadores'
                    valueEdit={bagMediatorLabel}
                    keyName='mediatorIds'
                    addInfo={selectBagMediators}
                    searchable
                    searchPlaceholder='Buscar mediador...'
                    multiple
                    selectedValues={bagForm.mediatorIds}
                    dropdownClassName='logistics-bag-dropdown'
                  />
                </label>
                <label className='logistics-review__field'>
                  <span>Código de la solicitud *</span>
                  <input
                    value={bagForm.code}
                    onChange={event => setBagForm(current => ({
                      ...current, code: event.target.value
                    }))}
                    maxLength='80'
                    required
                    placeholder='Ej. BOLSA-2026-001'
                  />
                </label>
                <label className='logistics-review__field'>
                  <span>Nombre del evento *</span>
                  <input
                    value={bagForm.eventName}
                    onChange={event => setBagForm(current => ({
                      ...current, eventName: event.target.value
                    }))}
                    maxLength='255'
                    required
                  />
                </label>
              </div>
            )}

            <footer>
              <button type='button' onClick={closeBagCreator} disabled={bagSaving}>Cancelar</button>
              <button type='submit' disabled={bagContextLoading || bagSaving}>
                {bagSaving ? 'Creando...' : 'Crear Bolsa'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {selected && (
        <div className='logistics-review' role='presentation' onMouseDown={event => {
          if (event.target === event.currentTarget) closeReview();
        }}>
          <form className='logistics-review__panel' onSubmit={submitReview} role='dialog' aria-modal='true' aria-labelledby='review-title'>
            <header>
              <div>
                <span className='logistics-dashboard__eyebrow'>REVISIÓN LOGÍSTICA</span>
                <h2 id='review-title'>{selected.eventName || `Solicitud de bolsa #${selected.deliveryNumber}`}</h2>
              </div>
              <button type='button' onClick={closeReview} aria-label='Cerrar'>×</button>
            </header>

            <RequestDetails item={selected} />

            <fieldset>
              <legend>Decisión *</legend>
              <label><input type='radio' name='action' value='accepted' checked={action === 'accepted'} onChange={() => setAction('accepted')} />Aceptar</label>
              <label><input type='radio' name='action' value='returned' checked={action === 'returned'} onChange={() => { setAction('returned'); setApprovalPdf(null); }} />Subsanar</label>
            </fieldset>

            <label className='logistics-review__field'>
              <span>Código de la solicitud {selected.requestType === 'new_requirement' && action === 'accepted' ? '*' : ''}</span>
              <input
                value={code}
                onChange={event => setCode(event.target.value)}
                readOnly={selected.requestType === 'bag_request'}
                required={selected.requestType === 'new_requirement' && action === 'accepted'}
                placeholder={selected.requestType === 'bag_request' ? '' : 'Ej. REQ-2026-001'}
              />
              {selected.requestType === 'bag_request' && <small>Generado automáticamente al crear la solicitud.</small>}
            </label>

            {action === 'accepted' && (
              <div className='logistics-review__field'>
                <span>{selected.hasApprovalPdf ? 'Reemplazar lista de chequeo (opcional)' : 'Lista de chequeo (opcional)'}</span>
                {selected.hasApprovalPdf && !approvalPdf && (
                  <div className='logistics-review__uploaded-file' role='status'>
                    <span className='material-symbols-rounded'>check_circle</span>
                    <div>
                      <strong>Archivo cargado</strong>
                      <small>{selected.approvalPdfName || 'Lista de chequeo en PDF'}</small>
                    </div>
                  </div>
                )}
                <input
                  aria-label={selected.hasApprovalPdf ? 'Reemplazar lista de chequeo' : 'Cargar lista de chequeo'}
                  type='file'
                  accept='.pdf,application/pdf'
                  onChange={selectChecklist}
                />
                <small>
                  {approvalPdf
                    ? `Nuevo archivo: ${approvalPdf.name}`
                    : selected.hasApprovalPdf
                      ? 'Selecciona un PDF únicamente si deseas reemplazar el archivo actual. Máximo 4 MB.'
                      : 'Puedes adjuntar la lista de chequeo ahora o después de aceptar. PDF de máximo 4 MB.'}
                </small>
              </div>
            )}

            <label className='logistics-review__field'>
              <span>Observación {action === 'returned' ? '*' : '(opcional)'}</span>
              <textarea value={observation} onChange={event => setObservation(event.target.value)} required={action === 'returned'} />
            </label>

            <footer>
              <button type='button' onClick={closeReview}>Cancelar</button>
              <button
                type='submit'
                className={action === 'returned' ? 'is-return' : ''}
                disabled={saving || (selected.status === 'accepted' && action === 'accepted' && !approvalPdf)}>
                {saving
                  ? 'Guardando...'
                  : selected.status === 'accepted' && action === 'accepted'
                    ? 'Guardar lista de chequeo'
                    : action === 'accepted' ? 'Aceptar solicitud' : 'Solicitar subsanación'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {historyRequest && (
        <div className='logistics-review' role='presentation' onMouseDown={event => {
          if (event.target === event.currentTarget) closeHistory();
        }}>
          <section className='logistics-review__panel logistics-review__panel--history' role='dialog' aria-modal='true' aria-labelledby='history-title'>
            <header>
              <div>
                <span className='logistics-dashboard__eyebrow'>TRAZABILIDAD</span>
                <h2 id='history-title'>{historyRequest.eventName || `Solicitud de bolsa #${historyRequest.deliveryNumber}`}</h2>
              </div>
              <button type='button' onClick={closeHistory} aria-label='Cerrar'>×</button>
            </header>
            <RequestHistory entries={history} loading={historyLoading} />
          </section>
        </div>
      )}
    </section>
  );
}

function RequestHistory({ entries, loading }) {
  return (
    <section className='logistics-history'>
      <h3>Historial del requerimiento</h3>
      {loading ? (
        <p className='logistics-history__empty'>Cargando historial...</p>
      ) : entries.length === 0 ? (
        <p className='logistics-history__empty'>No hay movimientos registrados.</p>
      ) : (
        <ol>
          {entries.map(entry => (
            <li key={entry.id}>
              <span className={`logistics-history__marker logistics-history__marker--${entry.action}`}></span>
              <div>
                <strong>{HISTORY_LABELS[entry.action] || entry.action}</strong>
                <p>{entry.actorName || 'Usuario'} · {formatHistoryDate(entry.createdAt)}</p>
                {entry.fromStatus && entry.fromStatus !== entry.toStatus && (
                  <small>{STATUS_LABELS[entry.fromStatus] || entry.fromStatus} → {STATUS_LABELS[entry.toStatus] || entry.toStatus}</small>
                )}
                {entry.observation && <blockquote>{entry.observation}</blockquote>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function formatHistoryDate(value) {
  if (!value) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Bogota'
  }).format(new Date(value));
}

function RequestDetails({ item }) {
  const common = [
    ['Responsable', item.responsibleName], ['Teléfono', item.phone],
    ['Lugar', item.place], ['Dirección', item.address], ['Hora', item.requestTime]
  ];
  const details = item.requestType === 'new_requirement'
    ? [
        ['Objetivo', item.eventObjective], ['Descripción', item.shortDescription],
        ['Solicitud', item.requestDetails],
        ['Comuna', item.municipalityName],
        ['Beneficiario', item.beneficiaryName]
      ]
    : [
        ['# de entrega', item.deliveryNumber], ['Fecha', item.requestDate],
        ['Cantidad', item.quantity]
      ];
  return (
    <dl className='logistics-review__details'>
      {[...common, ...details].map(([label, value]) => (
        <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>
      ))}
    </dl>
  );
}

export default LogisticsDashboard;
